import { Lock, RotateCw, Trash2, Unlock } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // <-- NEU
import { GRADING_THRESHOLDS } from '../../../../../lib/constants.js';
import { cn } from '../../../../../lib/utils.js';
import type { UnifiedBoundingBox } from '../utils.js';

interface InteractiveBoxProps {
  box: UnifiedBoundingBox;
  isSelected: boolean;
  isApproved: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onChange: (newBox: UnifiedBoundingBox) => void;
  onChangeEnd?: (newBox: UnifiedBoundingBox) => void;
  onSelect: () => void;
  onDelete?: () => void;
  onToggleLock?: () => void;
  onRetry?: () => void | Promise<void>;
}

export function InteractiveBox({
  box,
  isSelected,
  isApproved,
  containerRef,
  onChange,
  onChangeEnd,
  onSelect,
  onDelete,
  onToggleLock,
  onRetry,
}: InteractiveBoxProps) {
  const { t } = useTranslation(); // <-- NEU
  // Server-derived (BoundingBox.ocrRerunning) — see its doc comment for why this isn't client state:
  // correct the instant this component mounts, regardless of whether it was around when the request
  // that set it started.
  const isSaving = box.ocrRerunning === true;

  const handleRetry = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSaving) return;
    void onRetry?.();
  };
  const isBoxLocked = box.approved === true;
  const boxConfidence = Number(box.confidence);
  const isLowConfidence =
    !isApproved &&
    box.approved !== true &&
    Number.isFinite(boxConfidence) &&
    boxConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE;
  const isMediumConfidence =
    !isApproved &&
    box.approved !== true &&
    Number.isFinite(boxConfidence) &&
    boxConfidence >= GRADING_THRESHOLDS.LOW_CONFIDENCE &&
    boxConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;

  const startInteraction = (e: React.PointerEvent, action: string) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();

    if (isBoxLocked) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startBox = { ...box };
    let latestBox = { ...box };

    const normalizeBox = (candidate: UnifiedBoundingBox): UnifiedBoundingBox => {
      const minSize = 1;
      const maxX = 100;
      const maxY = 100;

      const width = Math.max(minSize, Math.min(candidate.width, maxX));
      const height = Math.max(minSize, Math.min(candidate.height, maxY));
      const x = Math.max(0, Math.min(candidate.x, maxX - width));
      const y = Math.max(0, Math.min(candidate.y, maxY - height));

      return {
        ...candidate,
        x,
        y,
        width,
        height,
      };
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const currentScale = rect.width / 800;
      const deltaPxX = (moveEvent.clientX - startX) / currentScale;
      const deltaPxY = (moveEvent.clientY - startY) / currentScale;
      const deltaPctX = (deltaPxX / 800) * 100;
      const deltaPctY = (deltaPxY / (rect.height / currentScale)) * 100;

      const newBox = { ...startBox };

      if (action === 'move') {
        newBox.x += deltaPctX;
        newBox.y += deltaPctY;
      } else {
        if (action.includes('n')) {
          newBox.y += deltaPctY;
          newBox.height -= deltaPctY;
        }
        if (action.includes('s')) newBox.height += deltaPctY;
        if (action.includes('w')) {
          newBox.x += deltaPctX;
          newBox.width -= deltaPctX;
        }
        if (action.includes('e')) newBox.width += deltaPctX;
      }

      if (newBox.width > 1 && newBox.height > 1) {
        const normalized = normalizeBox(newBox);
        latestBox = normalized;
        onChange(normalized);
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      onChangeEnd?.(latestBox);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClass = cn(
    'absolute w-3 h-3 rounded-full z-10 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center',
    isLowConfidence ? 'bg-red-600' : isMediumConfidence ? 'bg-amber-600' : 'bg-blue-600',
  );

  return (
    <div
      className={cn(
        'absolute border-2 transition-colors cursor-move group',
        isLowConfidence
          ? isSelected
            ? 'border-red-600 bg-red-500/30 z-20'
            : 'border-red-400 bg-red-400/10 hover:bg-red-400/20 z-10'
          : isMediumConfidence
            ? isSelected
              ? 'border-amber-600 bg-amber-500/30 z-20'
              : 'border-amber-400 bg-amber-400/10 hover:bg-amber-400/20 z-10'
            : isSelected
              ? 'border-blue-600 bg-blue-500/30 z-20'
              : 'border-blue-400 bg-blue-400/10 hover:bg-blue-400/20 z-10',
      )}
      style={{
        left: `${box.x}%`,
        top: `${box.y}%`,
        width: `${box.width}%`,
        height: `${box.height}%`,
      }}
      onPointerDown={e => startInteraction(e, 'move')}
    >
      <div
        className={cn(handleClass, '-top-1.5 -left-1.5 cursor-nwse-resize')}
        onPointerDown={e => startInteraction(e, 'nw')}
      />
      <div
        className={cn(handleClass, '-top-1.5 -right-1.5 cursor-nesw-resize')}
        onPointerDown={e => startInteraction(e, 'ne')}
      />
      <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
        <div className="flex items-center bg-white dark:bg-card border border-slate-200 dark:border-slate-700 rounded-md shadow-sm overflow-hidden">
          <button
            type="button"
            className="p-1.5 text-slate-600 dark:text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            onPointerDown={e => {
              e.preventDefault();
              e.stopPropagation();
              onDelete?.();
            }}
            title={t('Canvas.grading.deleteBox')}
          >
            <Trash2 size={12} />
          </button>
          <button
            type="button"
            className={cn(
              'p-1.5 transition-colors border-l border-slate-200 dark:border-slate-700',
              isBoxLocked
                ? 'text-amber-700 dark:text-amber-400 hover:text-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                : 'text-slate-600 dark:text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
            )}
            onPointerDown={e => {
              e.preventDefault();
              e.stopPropagation();
              onToggleLock?.();
            }}
            title={isBoxLocked ? t('Canvas.grading.unlockBox') : t('Canvas.grading.lockBox')}
          >
            {isBoxLocked ? <Unlock size={12} /> : <Lock size={12} />}
          </button>
          {onRetry && !isBoxLocked && (
            <button
              type="button"
              className="p-1.5 border-l border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors disabled:opacity-60"
              onPointerDown={handleRetry}
              disabled={isSaving}
              title={t('Canvas.grading.retryOcr')}
            >
              <RotateCw size={12} className={cn(isSaving && 'animate-spin')} />
            </button>
          )}
        </div>
      </div>
      <div
        className={cn(handleClass, '-bottom-1.5 -left-1.5 cursor-nesw-resize')}
        onPointerDown={e => startInteraction(e, 'sw')}
      />
      <div
        className={cn(handleClass, '-bottom-1.5 -right-1.5 cursor-nwse-resize')}
        onPointerDown={e => startInteraction(e, 'se')}
      />
    </div>
  );
}
